/** @jsxImportSource @emotion/react */

import { FC, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Button from '@mui/material/Button'
import { keyBy } from 'lodash'
import {
  findOneScheduler,
  findOneSchedulerVariables,
  findOneScheduler_findOneScheduler_task as Task,
} from '__generatedTypes__/findOneScheduler'
import { FIND_SCHEDULER, UPDATE_SCHEDULER } from 'queries/customSchedule'
import { PageLoader } from 'components/PageLoader'
import { setFieldData } from 'utils/setFieldData'
import { toast } from 'utils/toast'
import { updateScheduler, updateSchedulerVariables } from '__generatedTypes__/updateScheduler'
import { UpdateSchedulerInput } from '__generatedTypes__/globalTypes'
import { ScheduleOrder } from '../ScheduleOrder'
import { PHASES } from '../ScheduleOrder/consts'
import { ScheduleAnalytics } from '../ScheduleAnalytics'
import { FormValues } from './consts'
import * as classes from './styles'

type Props = {
  platformId: string
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const Schedule: FC<Props> = ({ platformId }) => {
  const {
    data,
    loading: loadingScheduler,
    refetch,
  } = useQuery<findOneScheduler, findOneSchedulerVariables>(FIND_SCHEDULER, {
    variables: {
      platformId,
    },
  })

  const [updateScheduler] = useMutation<updateScheduler, updateSchedulerVariables>(UPDATE_SCHEDULER)

  const handleSubmit = async (values: FormValues) => {
    try {
      const { phases, tasks, phasesTitles } = Object.values(PHASES).reduce(
        (acc, phase, index) => {
          const key = `phase${index + 1}TaskIds` as keyof UpdateSchedulerInput

          const phaseTitleKey = `phase${index + 1}Title` as keyof FormValues

          const phaseTitle = values[phaseTitleKey]

          const phasesTitles = {
            ...acc.phasesTitles,
            [phaseTitleKey]: phaseTitle,
          }

          if (!values[phase]) {
            return {
              ...acc,
              phasesTitles,
              phases: {
                ...acc.phases,
                [key]: [],
              },
            }
          }

          return {
            phases: {
              ...acc.phases,
              [key]: values[phase].map((task) => task.id),
            },
            phasesTitles,
            tasks: [...acc.tasks, ...values[phase].map((task) => ({ id: task.id }))],
          }
        },
        {
          phases: {},
          phasesTitles: {},
          tasks: [] as { id: string }[],
        }
      )

      await updateScheduler({
        fetchPolicy: 'no-cache',
        variables: {
          scheduler: {
            id: data?.findOneScheduler?.id,
            ...phases,
            ...phasesTitles,
            task: tasks,
            platform: {
              id: platformId,
            },
          },
        },
      })
      await refetch()

      toast.success('Schedule updated successfully')
    } catch (error) {
      toast.error('Failed to update schedule')
      console.error('An error occurred while tying to update scheduler', error)
    }
  }

  const initialValues: FormValues = useMemo(() => {
    if (!data?.findOneScheduler) {
      return {
        [PHASES.PHASE1]: [],
        [PHASES.PHASE2]: [],
        [PHASES.PHASE3]: [],
        phase1Title: '',
        phase2Title: '',
        phase3Title: '',
      }
    }

    const taskById = keyBy(data.findOneScheduler.task, 'id')

    const {
      phase1TaskIds,
      phase2TaskIds = [],
      phase3TaskIds = [],
      phase1Title,
      phase2Title,
      phase3Title,
    } = data.findOneScheduler

    const getPhaseTasks = (phaseTaskIds: string[] | null, phase: number) => {
      if (!phaseTaskIds) {
        return []
      }

      const notFoundTasks: string[] = []

      const phaseTasks = phaseTaskIds
        .map((taskId) => {
          const task = taskById[taskId]

          if (!task) {
            notFoundTasks.push(taskId)
            return null
          }

          return {
            ...task,
          }
        })
        .filter(Boolean) as Task[]

      if (notFoundTasks.length > 0) {
        console.error(`Tasks with ids ${notFoundTasks.join(', ')} not found for Phase #${phase}`)
      }

      return phaseTasks
    }

    return {
      [PHASES.PHASE1]: getPhaseTasks(phase1TaskIds, 1),
      [PHASES.PHASE2]: getPhaseTasks(phase2TaskIds, 2),
      [PHASES.PHASE3]: getPhaseTasks(phase3TaskIds, 3),
      phase1Title: phase1Title ?? '',
      phase2Title: phase2Title ?? '',
      phase3Title: phase3Title ?? '',
    }
  }, [data?.findOneScheduler])

  if (loadingScheduler) {
    return <PageLoader />
  }

  return (
    <section>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} data-testid="phase-form">
            <ScheduleAnalytics />

            <ScheduleOrder platformId={platformId} />

            <div css={classes.saveButtonWrapper}>
              <Button css={classes.saveButton} color="blue" type="submit" size="large" disabled={submitting} fullWidth>
                Save Edits
              </Button>
            </div>
          </form>
        )}
      />
    </section>
  )
}
